import { fetchWrapper } from '@/reducers/fetch'
import { apiAxios } from '@/api/index'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ReduxState } from '@/utils/typeHelpers'
import { createSelector } from 'reselect'
import { times } from 'lodash'
import {
  addCurrencyArray,
  centsToCurrency,
  dollarsToCurrency,
} from '@/utils/currencyHelpers'

export interface UserIncomePlanSource {
  id: number
  incomePlanId: number
  createdAt: string
  updatedAt: string
  name: string
  source: 'therapy' | 'non_therapy'
  rateInCents: number
  numberPerMonth?: number | null
  cadence: 'hourly' | 'monthly'
  payerType?: string | null
}

export type CreateUserIncomePlanSource = Omit<
  UserIncomePlanSource,
  'id' | 'createdAt' | 'updatedAt' | 'incomePlanId'
>

export type AddUserIncomePlanSourceToForm = Omit<
  CreateUserIncomePlanSource,
  'rateInCents'
> & {
  rate: number
}

export interface UserIncomePlan {
  id: number
  createdAt: string
  updatedAt: string
  planName: string
  totalPlannedIncomeInCents: number
  therapyIncomeInCents: number
  nonTherapyIncomeInCents: number
  ratePerSession: number
  sessionsPerMonth: number
  incomePlanSources: UserIncomePlanSource[]
}

export type CreateUserIncomePlan = Pick<UserIncomePlan, 'planName'> & {
  planSources: CreateUserIncomePlanSource[]
}

export interface UserIncomePlanMonth {
  id: number
  createdAt: string
  updatedAt: string
  notes: string
  totalPlannedIncomeInCents?: number
  therapyIncomeInCents?: number
  nonTherapyIncomeInCents?: number
  ratePerSession?: number
  sessionsPerMonth?: number
  month: number
  year: number
  pastTotalBusinessIncomeInCents?: number
  currentTotalBusinessIncomeInCents?: number
}

const initialState: {
  userIncomePlans: {
    [id: number]: UserIncomePlan
  }
  userIncomePlanMonths: {
    [yearMonth: string]: UserIncomePlanMonth
  }
} = {
  userIncomePlans: {},
  userIncomePlanMonths: {},
}

const incomePlanningSlice = createSlice({
  name: 'incomePlanning',
  initialState,
  reducers: {
    setIncomePlan: (state, action: PayloadAction<UserIncomePlan>) => {
      state.userIncomePlans[action.payload.id] = action.payload
    },
    setManyIncomePlans: (state, action: PayloadAction<UserIncomePlan[]>) => {
      action.payload.forEach((plan) => {
        state.userIncomePlans[plan.id] = plan
      })
    },
    deleteIncomePlan: (state, action: PayloadAction<{ deletedId: number }>) => {
      delete state.userIncomePlans[action.payload.deletedId]
    },
    setIncomePlanMonth: (state, action: PayloadAction<UserIncomePlanMonth>) => {
      state.userIncomePlanMonths[
        `${action.payload.year}-${action.payload.month}`
      ] = {
        ...state.userIncomePlanMonths[
          `${action.payload.year}-${action.payload.month}`
        ],
        ...action.payload,
      }
    },
    setManyIncomePlanMonths: (
      state,
      action: PayloadAction<UserIncomePlanMonth[]>
    ) => {
      action.payload.forEach((month) => {
        state.userIncomePlanMonths[`${month.year}-${month.month}`] = {
          ...state.userIncomePlanMonths[`${month.year}-${month.month}`],
          ...month,
        }
      })
    },
  },
})

export default incomePlanningSlice.reducer

const {
  setIncomePlanMonth,
  setManyIncomePlanMonths,
  setManyIncomePlans,
  setIncomePlan,
  deleteIncomePlan,
} = incomePlanningSlice.actions

export const selectUserIncomePlanState = (state: ReduxState) =>
  state.incomePlanning.userIncomePlanMonths

export const selectIncomePlanningMonthsForYear = createSelector(
  selectUserIncomePlanState,
  (_: unknown, year: number) => year,
  (months, year) =>
    times(12).map((i) => {
      // times is 0 indexed
      const month = i + 1

      return (
        months[`${year}-${month}`] || {
          notes: '',
          month,
          year,
        }
      )
    })
)

// Planned income for user usually comes from financial profile but if every
// month for a year is filled out it can be gathered from income planning.  This
// value can replace the planned income value in goal update flow
export const selectIncomePlanningPlannedIncomeForYear = createSelector(
  selectIncomePlanningMonthsForYear,
  (months) =>
    dollarsToCurrency(
      addCurrencyArray(
        months.map((month) =>
          centsToCurrency(month.totalPlannedIncomeInCents || 0)
        )
      )
    )
)

export const selectUserIncomePlans = (state: ReduxState) =>
  state.incomePlanning.userIncomePlans

export const selectUserIncomePlanById = createSelector(
  selectUserIncomePlans,
  (_: unknown, id: number | undefined | null) => id,
  (plans, id) => (id && plans[id] ? plans[id] : null)
)

export const FETCH_USER_INCOME_PLANS_KEY = 'FETCH_USER_INCOME_PLANS_KEY'
export const fetchUserIncomePlans = () =>
  fetchWrapper({
    fetchKey: FETCH_USER_INCOME_PLANS_KEY,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.get<UserIncomePlan[]>(
        '/finances/api/v1/user_income_planning/user_income_plans'
      )

      dispatch(setManyIncomePlans(json.data))

      return json.data
    },
  })

export const fetchUserIncomePlan = (id: number) =>
  fetchWrapper({
    fetchFunction: async () => {
      const json = await apiAxios.get<UserIncomePlan>(
        `/finances/api/v1/user_income_planning/user_income_plans/${id}`
      )

      return json.data
    },
  })

export const CREATE_USER_INCOME_PLAN_KEY = 'CREATE_USER_INCOME_PLAN_KEY'
export const createUserIncomePlan = (data: CreateUserIncomePlan) =>
  fetchWrapper({
    fetchKey: CREATE_USER_INCOME_PLAN_KEY,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.post<UserIncomePlan>(
        '/finances/api/v1/user_income_planning/user_income_plans',
        data
      )

      dispatch(setIncomePlan(json.data))

      return json.data
    },
  })

export const UPDATE_USER_INCOME_PLAN_KEY = 'UPDATE_USER_INCOME_PLAN_KEY'
export const updateUserIncomePlan = (
  id: number,
  data: Partial<UserIncomePlan> & { planSources: CreateUserIncomePlanSource[] }
) =>
  fetchWrapper({
    fetchKey: UPDATE_USER_INCOME_PLAN_KEY,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.put<UserIncomePlan>(
        `/finances/api/v1/user_income_planning/user_income_plans/${id}`,
        data
      )

      dispatch(setIncomePlan(json.data))

      return json.data
    },
  })

export const deleteUserIncomePlan = (id: number) =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.delete<{ deletedId: number }>(
        `/finances/api/v1/user_income_planning/user_income_plans/${id}`
      )

      dispatch(deleteIncomePlan(json.data))

      return json.data
    },
  })

export const fetchUserIncomeMonthsForYear = (year: number) =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.get<{
        incomeMonths: Array<
          UserIncomePlanMonth & {
            pastTotalBusinessIncomeInCents: number
            currentTotalBusinessIncomeInCents: number
          }
        >
        pastYearTotalBusinessIncomeInCents: number
        currentYearTotalBusinessIncomeInCents: number
      }>(`/finances/api/v1/user_income_planning/user_income_months/${year}`)

      dispatch(setManyIncomePlanMonths(json.data.incomeMonths))

      return json.data
    },
  })

export const upsertUserIncomeMonth = (
  data: Partial<UserIncomePlanMonth> & { year: number; month: number }
) =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.put<UserIncomePlanMonth>(
        `/finances/api/v1/user_income_planning/user_income_months/${data.year}/${data.month}`,
        data
      )

      dispatch(setIncomePlanMonth(json.data))

      return json.data
    },
  })

export const BULK_UPDATE_USER_INCOME_MONTHS_KEY =
  'BULK_UPDATE_USER_INCOME_MONTHS_KEY'
export const bulkUpdateUserIncomeMonths = (data: {
  planId: number
  months: Array<{ month: number; year: number }>
}) =>
  fetchWrapper({
    fetchKey: BULK_UPDATE_USER_INCOME_MONTHS_KEY,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.post<UserIncomePlanMonth[]>(
        '/finances/api/v1/user_income_planning/user_income_months/bulk_update',
        data
      )

      dispatch(setManyIncomePlanMonths(json.data))

      return json.data
    },
  })
